import { useState } from 'react';
import {
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Grid,
  Link as MuiLink,
} from '@material-ui/core';
import { Finding, Severity, FindingStatus, SEVERITY_ORDER } from '../utils/types';
import { getSeverityColor } from '../utils/severity';
import {
  sortFindings,
  filterFindings,
  paginateFindings,
  SortColumn,
  SortDirection,
  PAGE_SIZE,
} from '../utils/findings-table-logic';
import { buildFindingUrl } from '../utils/url-builder';

interface FindingsTableProps {
  findings: Finding[];
  baseUrl: string;
}

const ALL_STATUSES: FindingStatus[] = [
  'Active', 'Verified', 'False Positive', 'Mitigated', 'Out of Scope', 'Risk Accepted',
];

export const FindingsTable = ({ findings, baseUrl }: FindingsTableProps) => {
  const [sortCol, setSortCol] = useState<SortColumn>('severity');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [sevFilter, setSevFilter] = useState<Severity | ''>('');
  const [statusFilter, setStatusFilter] = useState<FindingStatus | ''>('');
  const [hideMitigated, setHideMitigated] = useState(false);
  const [page, setPage] = useState(0);

  const filtered = filterFindings(
    findings,
    sevFilter || undefined,
    statusFilter || undefined,
  ).filter(f => !hideMitigated || !f.is_mitigated);
  const sorted = sortFindings(filtered, sortCol, sortDir);
  const pageData = paginateFindings(sorted, page);

  const handleSort = (col: SortColumn) => {
    if (col === sortCol) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(0);
  };

  const columns: { id: SortColumn; label: string }[] = [
    { id: 'title', label: 'Título' },
    { id: 'severity', label: 'Severidade' },
    { id: 'status', label: 'Status' },
    { id: 'date', label: 'Data' },
  ];

  return (
    <Card>
      <CardHeader title="Findings" />
      <Grid container spacing={2} style={{ padding: '0 16px 16px' }}>
        <Grid item xs={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Severidade</InputLabel>
            <Select
              value={sevFilter}
              onChange={e => { setSevFilter(e.target.value as Severity | ''); setPage(0); }}
              label="Severidade"
            >
              <MenuItem value="">Todas</MenuItem>
              {SEVERITY_ORDER.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value as FindingStatus | ''); setPage(0); }}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              {ALL_STATUSES.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3} style={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideMitigated}
                onChange={e => { setHideMitigated(e.target.checked); setPage(0); }}
                color="primary"
              />
            }
            label="Ocultar mitigados"
          />
        </Grid>
      </Grid>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.id}>
                  <TableSortLabel
                    active={sortCol === col.id}
                    direction={sortCol === col.id ? sortDir : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageData.map(f => (
              <TableRow key={f.id}>
                <TableCell>
                  <MuiLink
                    href={buildFindingUrl(baseUrl, f.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {f.title}
                  </MuiLink>
                </TableCell>
                <TableCell>
                  <Chip
                    label={f.severity}
                    size="small"
                    style={{
                      backgroundColor: getSeverityColor(f.severity),
                      color: '#fff',
                    }}
                  />
                </TableCell>
                <TableCell>{f.status}</TableCell>
                <TableCell>{f.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
      />
    </Card>
  );
};
